import React, { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import Reveal from "../common/Reveal";

/**
 * The nine real logo files in /public/images/Clients/.
 *
 * Intrinsic sizes are recorded here so every <img> can declare width/height
 * and reserve its box before the PNG decodes. Two families exist:
 * 133x32 (1-6.png) and 143x27 (the Untitled-1Artboard files). They are
 * normalised optically by a shared box + object-contain rather than by
 * matching heights, which would make the wider family visually dominate.
 *
 * The filenames carry no company names, and inventing them is not an
 * option, so the logos are rendered as decorative images (alt="") and the
 * meaning is carried by the section heading plus an aria-label on the
 * strip. That is the honest reading: a screen reader hears the claim once,
 * not "Client logo" eighteen times.
 */
const CLIENT_LOGOS = [
  { src: "/images/Clients/1.png", width: 133, height: 32 },
  { src: "/images/Clients/2.png", width: 133, height: 32 },
  { src: "/images/Clients/3.png", width: 133, height: 32 },
  { src: "/images/Clients/4.png", width: 133, height: 32 },
  { src: "/images/Clients/5.png", width: 133, height: 32 },
  { src: "/images/Clients/6.png", width: 133, height: 32 },
  { src: "/images/Clients/Untitled-1Artboard-1-copy-2.png", width: 143, height: 27 },
  { src: "/images/Clients/Untitled-1Artboard-1-copy-3.png", width: 143, height: 27 },
  { src: "/images/Clients/Untitled-1Artboard-1-copy-4.png", width: 143, height: 27 },
];

// Quiet by default, full colour on hover. The strip pauses on hover, so
// unlike before the hover state is actually reachable.
const LOGO_CLASSES =
  "h-8 w-28 sm:h-9 sm:w-32 object-contain grayscale opacity-60 " +
  "transition-[opacity,filter] duration-300 ease-out-expo " +
  "hover:grayscale-0 hover:opacity-100";

const Logo = ({ src, width, height }) => (
  <img
    src={src}
    alt=""
    aria-hidden="true"
    width={width}
    height={height}
    // 19 KB for all nine; eager avoids logos popping in mid-marquee, where
    // lazy loading behaves unreliably because the row is transform-animated.
    loading="eager"
    decoding="async"
    className={LOGO_CLASSES}
  />
);

/** Tracks prefers-reduced-motion and keeps tracking if the user changes it. */
const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();

    if (query.addEventListener) {
      query.addEventListener("change", update);
      return () => query.removeEventListener("change", update);
    }
    // Safari < 14
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);

  return reduced;
};

const Clients = () => {
  const reducedMotion = useReducedMotion();
  const bandRef = useRef(null);
  const [autoRunning, setAutoRunning] = useState(true);
  const [userPaused, setUserPaused] = useState(false);
  const running = autoRunning && !userPaused;

  // An infinite marquee that keeps animating off-screen or in a background
  // tab costs battery for nothing. Pause it in both cases.
  useEffect(() => {
    if (reducedMotion) return undefined;

    const node = bandRef.current;
    if (!node) return undefined;

    let onScreen = true;
    const sync = () => setAutoRunning(onScreen && !document.hidden);

    const observer =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(
            (entries) => {
              onScreen = entries[0].isIntersecting;
              sync();
            },
            { threshold: 0 }
          );

    if (observer) observer.observe(node);
    document.addEventListener("visibilitychange", sync);

    return () => {
      if (observer) observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
    };
  }, [reducedMotion]);

  return (
    <section
      aria-labelledby="clients-heading"
      className="border-t border-ink-200 bg-white py-12 sm:py-14 lg:py-16"
    >
      <div className="container-custom">
        {/* Label left, strip right on large screens — an editorial band
            rather than a centred stack. Deliberately shorter vertical
            rhythm than .section-padding: this is a trust signal, not a
            section of the page. */}
        <div className="grid items-center gap-7 lg:grid-cols-[minmax(0,13rem)_minmax(0,1fr)] lg:gap-12">
          <Reveal>
            {/* The eyebrow is the section's real heading, so the landmark
                has a name without adding visual weight. Owner's copy. */}
            <h2 id="clients-heading" className="eyebrow">
              <span aria-hidden="true" className="h-px w-6 bg-brand-400" />
              Trusted by innovative companies
            </h2>

            {/* WCAG 2.2.2 — the strip moves on its own for longer than five
                seconds, so it needs an always-visible, keyboard-reachable
                way to stop it. Hover is not a mechanism. Same control as
                the process walkthrough and the testimonial rail. Hidden
                under reduced motion, where nothing moves to pause. */}
            {reducedMotion ? null : (
              // .eyebrow makes the h2 inline-flex, so the control needs a
              // block wrapper to sit on its own line rather than flowing
              // alongside the heading text.
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setUserPaused((was) => !was)}
                  aria-label={
                    userPaused
                      ? "Play the client logo strip"
                      : "Pause the client logo strip"
                  }
                  className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white px-3 py-1.5 font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-500 transition-colors duration-200 hover:border-brand-300 hover:text-brand-700"
                >
                  {userPaused ? (
                    <Play className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <Pause className="h-3 w-3" aria-hidden="true" />
                  )}
                  {userPaused ? "Play" : "Pause"}
                </button>
              </div>
            )}
          </Reveal>

          <Reveal delay={90} className="min-w-0">
            {reducedMotion ? (
              // Reduced motion: no scroll at all. A static wrapped row shows
              // every logo at once, which is strictly more information.
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 lg:justify-start">
                {CLIENT_LOGOS.map((logo) => (
                  <Logo key={logo.src} {...logo} />
                ))}
              </div>
            ) : (
              <div
                ref={bandRef}
                role="img"
                aria-label="Client logos"
                className="clients-container group relative overflow-hidden"
              >
                {/*
                  Seamless loop maths: the keyframe translates -50% of a
                  max-content row, so one set must be exactly half the track.
                  The old `space-x-16` gave 17 gaps across 18 items, leaving
                  the loop 32px short and snapping every cycle. Spacing now
                  lives on every item as padding-right — including the last —
                  so two identical sets are exactly 2x one set, gaps included,
                  at every breakpoint.
                */}
                <div
                  className="animate-scroll flex items-center py-2 group-hover:[animation-play-state:paused]"
                  // Only set play-state when paused, so the hover class can
                  // still win while the marquee is otherwise running.
                  style={running ? undefined : { animationPlayState: "paused" }}
                >
                  {[0, 1].flatMap((copy) =>
                    CLIENT_LOGOS.map((logo) => (
                      <div
                        key={`${copy}-${logo.src}`}
                        className="flex shrink-0 items-center justify-center pr-10 sm:pr-14 lg:pr-16"
                      >
                        <Logo {...logo} />
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Clients;
